import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "flex flex-col gap-3" }
const _hoisted_2 = {
  key: 0,
  class: "flex w-full flex-col gap-2"
}
const _hoisted_3 = { class: "border-secondary flex flex-col gap-3 rounded-md border p-2.5" }

import { InputRow, MultiCombobox, DatePicker, Select, Combobox } from '@/components/ui/form';
    import { Textarea } from '@/components/ui/textarea';
    import { Input } from '@/components/ui/input';
    import { optionsDays, optionsYesNo } from '@/utils/form';
    import { computed } from 'vue';
    import { useI18n } from 'vue-i18n';
    import type { SelectOption } from '@/types';
    import { useTaskResponsiblesData } from '@/composables/useTaskResponsiblesData';
    import { useTaskVoucherData } from '@/composables/useTaskVoucherData';
    import FileView from '@/components/FileView.vue';
    import axios from 'axios';
    import { useQueryClient } from '@tanstack/vue-query';
    import QueryKey from '@/enums/QueryKey';
    import { usePage } from '@inertiajs/vue3';
    import type { SharedData } from '@/types';

    
export default /*@__PURE__*/_defineComponent({
  __name: 'TaskForm',
  props: {
    form: {}
  },
  setup(__props: any) {

    const props = __props;

    const queryClient = useQueryClient();

    const { taskResponsibles, isTaskResponsiblesPending } = useTaskResponsiblesData();
    const { taskVouchers, isTaskVouchersPending } = useTaskVoucherData(props.form.task_id);

    const page = usePage<SharedData>();
    const isClient = !!page.props.auth.user.is_client;

    const { t, tm } = useI18n();

    const statusOptions = computed<SelectOption[]>(() => {
        const map = tm('task-templates.statuses') as Record<string, string>;
        return Object.entries(map).map(([value, label]) => ({ value, label }));
    });

    const frequencyOptions = computed<SelectOption[]>(() => {
        const map = tm('task-templates.frequency_options') as Record<string, string>;
        return Object.entries(map).map(([value, label]) => ({ value, label }));
    });

    const weekOptions = computed<SelectOption[]>(() => {
        const map = tm('task-templates.odd_or_even_week_options') as Record<string, string>;
        return Object.entries(map).map(([value, label]) => ({ value, label }));
    });

    const monthOptions = computed<SelectOption[]>(() => {
        const map = tm('task-templates.start_or_end_month_options') as Record<string, string>;
        return Object.entries(map).map(([value, label]) => ({ value, label }));
    });

    const responsibleOptions = computed<SelectOption[]>(() => {
        const { responsibleCompanyUsers = {}, responsibleEmployeesKeepers = {} } =
            taskResponsibles.value || {};

        const hasCompanies = !!Object.keys(responsibleCompanyUsers).length;
        const hasEmployees = !!Object.keys(responsibleEmployeesKeepers).length;

        const makeGroup = (labelKey: string, entries: Record<string, unknown>) => [
            { is_group: true, label: t(labelKey), value: '' },
            ...Object.entries(entries).map(([value, label]) => ({ value, label: label as string })),
        ];

        const options: SelectOption[] = [];
        if (hasEmployees) options.push(...makeGroup('menu.employees', responsibleEmployeesKeepers));
        if (hasCompanies && hasEmployees)
            options.push({ is_seperator: true, value: '', label: '' });
        if (hasCompanies) options.push(...makeGroup('companies.clients', responsibleCompanyUsers));

        return options;
    });

    const files = computed(() => {
        return taskVouchers.value.map((file: any) => ({
            name: file.filename,
            url: `/uploads/${file.filename}`,
            id: file.task_voucher_id,
        }));
    });

    const handleDeleteFile = (file: any) => {
        axios.delete(route('v2.dashboard.tasks.vouchers.delete', file.id)).then((response) => {
            queryClient.invalidateQueries({
                queryKey: [QueryKey.DASHBOARD_TASKS_VOUCHERS, props.form.task_id],
            });
        });
    };

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(InputRow), {
      form: _ctx.form,
      name: "task_title",
      label: "dashboards.tasks_title"
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(Input), {
          id: "task_title",
          modelValue: _ctx.form.task_title,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_ctx.form.task_title) = $event)),
          autocomplete: "task_title",
          placeholder: _ctx.$t('dashboards.tasks_title'),
          disabled: isClient
        }, null, 8, ["modelValue", "placeholder"])
      ]),
      _: 1
    }, 8, ["form"]),
    _createVNode(_unref(InputRow), {
      form: _ctx.form,
      name: "task_description",
      label: "dashboards.tasks_description"
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(Textarea), {
          id: "task_description",
          modelValue: _ctx.form.task_description,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_ctx.form.task_description) = $event)),
          placeholder: _ctx.$t('dashboards.tasks_description'),
          disabled: isClient
        }, null, 8, ["modelValue", "placeholder"])
      ]),
      _: 1
    }, 8, ["form"]),
    _createVNode(_unref(InputRow), {
      form: _ctx.form,
      name: "deadline",
      label: "dashboards.tasks_deadline"
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(DatePicker), {
          modelValue: _ctx.form.deadline,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_ctx.form.deadline) = $event)),
          disabled: isClient
        }, null, 8, ["modelValue"])
      ]),
      _: 1
    }, 8, ["form"]),
    _createVNode(_unref(InputRow), {
      form: _ctx.form,
      name: "weekdays",
      label: "dashboards.notification.day_in_week"
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(MultiCombobox), {
          modelValue: _ctx.form.weekdays,
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((_ctx.form.weekdays) = $event)),
          options: _unref(optionsDays),
          translate: "",
          placeholder: _ctx.$t('dashboards.notification.day_in_week'),
          disabled: isClient
        }, null, 8, ["modelValue", "options", "placeholder"])
      ]),
      _: 1
    }, 8, ["form"]),
    _createVNode(_unref(InputRow), {
      form: _ctx.form,
      name: "filename",
      label: "task-templates.attach"
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(Input), {
          id: "filename",
          placeholder: _ctx.$t('task-templates.attach'),
          type: "file",
          onInput: _cache[4] || (_cache[4] = ($event: any) => (_ctx.form.filename = $event.target.files[0])),
          disabled: isClient
        }, null, 8, ["placeholder"])
      ]),
      _: 1
    }, 8, ["form"]),
    (files.value.length > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(files.value, (file) => {
            return (_openBlock(), _createBlock(FileView, {
              key: file.id,
              file: file,
              previewable: false,
              onDelete: handleDeleteFile
            }, null, 8, ["file"]))
          }), 128))
        ]))
      : _createCommentVNode("", true),
    _createVNode(_unref(InputRow), {
      form: _ctx.form,
      name: "task_status",
      label: "task-templates.task.status"
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(Select), {
          modelValue: _ctx.form.task_status,
          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((_ctx.form.task_status) = $event)),
          options: statusOptions.value,
          disabled: isClient
        }, null, 8, ["modelValue", "options"])
      ]),
      _: 1
    }, 8, ["form"]),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(_unref(InputRow), {
        form: _ctx.form,
        name: "send_notification",
        label: "task-templates.task.send_notification"
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(Select), {
            modelValue: _ctx.form.send_notification,
            "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((_ctx.form.send_notification) = $event)),
            options: _unref(optionsYesNo),
            translate: "",
            disabled: isClient
          }, null, 8, ["modelValue", "options"])
        ]),
        _: 1
      }, 8, ["form"]),
      _createVNode(_unref(InputRow), {
        form: _ctx.form,
        name: "notification_recurring_week",
        label: "dashboards.notification.frequency"
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(Select), {
            modelValue: _ctx.form.notification_recurring_week,
            "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((_ctx.form.notification_recurring_week) = $event)),
            options: frequencyOptions.value,
            disabled: isClient
          }, null, 8, ["modelValue", "options"])
        ]),
        _: 1
      }, 8, ["form"]),
      (_ctx.form.notification_recurring_week === '2')
        ? (_openBlock(), _createBlock(_unref(InputRow), {
            key: 0,
            form: _ctx.form,
            name: "notification_recurring_even_week",
            label: "dashboards.notification.choose_odd_or_even_week"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(Select), {
                modelValue: _ctx.form.notification_recurring_even_week,
                "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((_ctx.form.notification_recurring_even_week) = $event)),
                options: weekOptions.value,
                disabled: isClient
              }, null, 8, ["modelValue", "options"])
            ]),
            _: 1
          }, 8, ["form"]))
        : _createCommentVNode("", true),
      (_ctx.form.notification_recurring_week === '4')
        ? (_openBlock(), _createBlock(_unref(InputRow), {
            key: 1,
            form: _ctx.form,
            name: "notification_recurring_start_of_month",
            label: "dashboards.notification.choose_start_or_end_month"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(Select), {
                modelValue: _ctx.form.notification_recurring_start_of_month,
                "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((_ctx.form.notification_recurring_start_of_month) = $event)),
                options: monthOptions.value,
                disabled: isClient
              }, null, 8, ["modelValue", "options"])
            ]),
            _: 1
          }, 8, ["form"]))
        : _createCommentVNode("", true),
      _createVNode(_unref(InputRow), {
        form: _ctx.form,
        name: "responsible_ids",
        label: "task-templates.responsible"
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(Combobox), {
            modelValue: _ctx.form.responsible_ids,
            "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((_ctx.form.responsible_ids) = $event)),
            options: responsibleOptions.value,
            "show-remove-button": false,
            loading: _unref(isTaskResponsiblesPending),
            disabled: isClient,
            side: "top"
          }, null, 8, ["modelValue", "options", "loading"])
        ]),
        _: 1
      }, 8, ["form"])
    ])
  ]))
}
}

})